import React, { useState } from 'react';
import { Mail, Phone, MapPin, Instagram, Twitter, Linkedin, Facebook, Check, Copy, X, Shield, FileText } from 'lucide-react';

const Footer = () => {
  const [isCopied, setIsCopied] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  
  const colors = {
    primary: "#122721",
    background: "#6cf168",
    accent: "#36893B",
    light: "#f8f9fa",
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleNewsletterSubmit = async () => {
    if (!newsletterEmail) {
      setNewsletterError('Please enter your email');
      return;
    }

    setIsLoading(true);
    setNewsletterError(null);

    try {
      // HubSpot submission
      const portalId = '146261155';
      const formId = 'd6983bc2-7ed5-4676-889d-cea13185e7dc';
      const hubspotUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

      const payload = {
        fields: [
          {
            name: 'email',
            value: newsletterEmail
          },
          {
            name: 'firstname',
            value: 'Newsletter Subscriber'
          },
          {
            name: 'submission_source',
            value: 'Newsletter Signup - Footer'
          },
          {
            name: 'form_location',
            value: 'Footer'
          },
          {
            name: 'lead_type',
            value: 'Newsletter Subscriber'
          },
          {
            name: 'submission_notes',
            value: 'Newsletter subscription from website footer. Interested in UAE tax updates and financial management tips.'
          },
          {
            name: 'page_url',
            value: window.location.href
          }
        ],
        context: {
          pageUri: window.location.href,
          pageName: 'Newsletter Subscription from Footer'
        }
      };

      const response = await fetch(hubspotUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      console.log('HubSpot response:', response.status, responseText);

      if (response.ok) {
        setNewsletterSuccess(true);
        setNewsletterEmail('');
        setTimeout(() => {
          setNewsletterSuccess(false);
        }, 5000);
      } else {
        console.error('HubSpot error:', responseText);
        throw new Error(`Failed to submit: ${response.status}`);
      }
    } catch (error) {
      console.error('Newsletter submission error:', error);
      setNewsletterError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer style={{ backgroundColor: colors.primary }} className="text-white">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 pt-12 md:pt-16 pb-6 md:pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {/* Company Info */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            <div className="mb-4 md:mb-6">
              <img src="/2.png" alt="Arbaah" className="h-16 md:h-20" />
            </div>
            <p className="mb-4 text-gray-300 text-sm md:text-base">
              Voice-to-Invoice. Smart OCR. Real-time TAX alerts.
Your finance, fully automated. Built for UAE. 
Speak it. Scan it. Track it.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.instagram.com/arbaah.ae?igsh=Z2V2ZGl1bmljbjYz&utm_source=qr" className="text-white hover:text-green-300 transition-colors">
                <Instagram size={18} className="md:w-5 md:h-5" />
              </a>
              <a href="https://www.linkedin.com/company/arbaah/" className="text-white hover:text-green-300 transition-colors">
                <Linkedin size={18} className="md:w-5 md:h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-4 md:mb-6 text-green-300">Quick Links</h3>
            <ul className="space-y-2 md:space-y-3">
              <li><a href="#benefits" className="hover:text-green-300 transition-colors text-sm md:text-base">Features</a></li>
              <li><a href="#pricing" className="hover:text-green-300 transition-colors text-sm md:text-base">Pricing</a></li>
              <li><a href="#testimonials" className="hover:text-green-300 transition-colors text-sm md:text-base">Testimonials</a></li>
              <li><a href="#faq" className="hover:text-green-300 transition-colors text-sm md:text-base">FAQ</a></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-4 md:mb-6 text-green-300">Contact Us</h3>
            <ul className="space-y-3 md:space-y-4">
              <li className="flex items-center group">
                <Mail size={16} className="mr-2 md:mr-3 text-green-300 flex-shrink-0 md:w-[18px] md:h-[18px]" />
                <button
                  onClick={() => copyToClipboard('hello@arbaah.ae')}
                  className="flex items-center gap-2 hover:text-green-300 transition-all duration-200 cursor-pointer text-sm md:text-base"
                  title="Click to copy email"
                >
                  <span>hello@arbaah.ae</span>
                  <span className={`transition-all duration-300 ${isCopied ? 'opacity-100 scale-100' : 'opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100'}`}>
                    {isCopied ? (
                      <Check size={14} className="text-green-400 md:w-4 md:h-4" />
                    ) : (
                      <Copy size={14} className="text-gray-400 md:w-4 md:h-4" />
                    )}
                  </span>
                </button>
                {isCopied && (
                  <span className="ml-2 text-green-400 text-xs md:text-sm animate-pulse">
                    Copied!
                  </span>
                )}
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-4 md:mb-6 text-green-300">Stay Updated</h3>
            <p className="mb-4 text-gray-300 text-sm md:text-base">Subscribe to our newsletter for the latest updates on UAE tax regulations and financial management tips.</p>
            {newsletterSuccess ? (
              <div className="bg-green-600 bg-opacity-20 border border-green-400 text-green-300 px-3 py-2 rounded-lg text-sm md:text-base">
                🎉 Thank you for subscribing!
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  className="px-3 py-2 md:px-4 md:py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-gray-700 text-white text-sm md:text-base flex-1"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  disabled={isLoading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleNewsletterSubmit();
                    }
                  }}
                />
                <button
                  onClick={handleNewsletterSubmit}
                  className="px-3 py-2 md:px-4 md:py-2 rounded-lg font-medium bg-green-600 hover:bg-green-700 transition-colors text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
            )}
            {newsletterError && (
              <p className="text-red-400 text-xs md:text-sm mt-2">{newsletterError}</p>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-gray-700 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 mb-4 md:mb-0 text-sm md:text-base">
            &copy; {new Date().getFullYear()} Arbaah. All rights reserved.
          </p>
          <div className="flex space-x-4 md:space-x-6">
            <button onClick={() => setShowTerms(true)} className="text-gray-400 hover:text-white transition-colors text-sm md:text-base">
              Terms of Service
            </button>
            <button onClick={() => setShowPrivacy(true)} className="text-gray-400 hover:text-white transition-colors text-sm md:text-base">
              Privacy Policy
            </button>
          </div>
        </div>
      </div>

      {/* Legal Modals */}
      {showTerms && (
        <TermsModal onClose={() => setShowTerms(false)} />
      )}
      {showPrivacy && (
        <PrivacyModal onClose={() => setShowPrivacy(false)} />
      )}
    </footer>
  );
};

// Terms of Service Modal Component
const TermsModal = ({ onClose }) => {
  const colors = {
    primary: "#122721",
    background: "#6cf168",
    accent: "#36893B",
    light: "#f8f9fa",
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 md:p-8 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-green-600" />
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: colors.primary }}>Terms of Service</h2>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 md:px-8 md:py-8">
          <div className="prose prose-sm md:prose max-w-none">
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm md:text-base font-semibold text-green-800 mb-1">Effective Date: 27.05.2025</p>
              <p className="text-sm text-green-700">
                Platform: Arbaah (www.arbaah.ae)<br />
                Company: Arbaah<br />
                Trade License Number: 1511536<br />
                Email: hello@arbaah.ae
              </p>
            </div>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>1. Acceptance of Terms</h3>
              <p className="text-gray-700 mb-3">
                By accessing or using Arbaah, you agree to be bound by these Terms of Service. If you do not agree, please do not use the platform. These Terms are governed by the laws of the United Arab Emirates, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Federal Law No. 1 of 2006 on Electronic Transactions and Commerce</li>
                <li>Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data (PDPL)</li>
                <li>Federal Decree-Law No. 34 of 2021 on Cybercrimes</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>2. Platform Description</h3>
              <p className="text-gray-700 mb-3">Arbaah is a cloud-based SaaS platform offering business services including:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Invoice creation (manual and voice input)</li>
                <li>OCR-based receipt scanning and auto-categorization</li>
                <li>Real-time financial dashboards</li>
                <li>Document storage and reminders</li>
                <li>VAT & TRN compliance tools</li>
                <li>Financial reporting and analytics</li>
              </ul>
              <p className="text-gray-700 mt-3 font-medium">Important Disclaimer: Arbaah is a software platform only. We are not an accounting firm, legal entity, certified tax advisor, or licensed financial consultant. All financial decisions and compliance obligations remain your responsibility.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>3. User Eligibility</h3>
              <p className="text-gray-700 mb-3">You may use Arbaah only if you are:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>A business entity licensed in the UAE or GCC</li>
                <li>Legally authorized to operate in your jurisdiction</li>
                <li>At least 18 years old and capable of entering into contracts</li>
                <li>Authorized to enter financial data on behalf of your organization</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>4. Account & Usage Rules</h3>
              <div className="mb-3">
                <p className="text-gray-700 font-medium mb-2">You agree to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Provide accurate company and financial data</li>
                  <li>Use the platform only for legal business purposes</li>
                  <li>Keep login credentials secure and confidential</li>
                  <li>Ensure authorized access only by appropriate personnel</li>
                  <li>Maintain backup records of critical financial data</li>
                </ul>
              </div>
              <div>
                <p className="text-gray-700 font-medium mb-2">You agree not to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Upload false, forged, or misleading documents</li>
                  <li>Use the platform for illegal or fraudulent activities</li>
                  <li>Attempt to interfere with platform integrity or security</li>
                  <li>Share account access with unauthorized parties</li>
                  <li>Use the platform to violate any UAE or international laws</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>5. Data Ownership and Portability</h3>
              <p className="text-gray-700 mb-3"><strong>Your Data:</strong> All business data, documents, and information you input into Arbaah remains your property. Arbaah does not claim ownership of your business data.</p>
              <p className="text-gray-700 mb-3"><strong>Data Export:</strong> You may export your data at any time through the platform's export features. Upon account termination, you have 30 days to export your data before it may be deleted.</p>
              <p className="text-gray-700"><strong>Data Accuracy:</strong> You are solely responsible for the accuracy and completeness of all data entered into the platform.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>6. Service Availability and Performance</h3>
              <p className="text-gray-700 mb-3"><strong>Service Levels:</strong> We strive to maintain 99.5% uptime, but do not guarantee uninterrupted service availability.</p>
              <p className="text-gray-700 mb-3"><strong>Maintenance:</strong> We may perform scheduled maintenance with advance notice when possible.</p>
              <p className="text-gray-700"><strong>Service Modifications:</strong> We reserve the right to modify, update, or discontinue features with reasonable notice.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>7. Third-Party Integrations</h3>
              <p className="text-gray-700 mb-3">Arbaah may integrate with third-party services including:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Banking institutions for transaction imports</li>
                <li>Payment processors</li>
                <li>Government systems for VAT/TRN compliance</li>
                <li>Cloud storage providers</li>
              </ul>
              <p className="text-gray-700">You acknowledge that such integrations are subject to third-party terms and availability.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>8. Subscription & Payment</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Subscription plans are billed monthly or annually</li>
                <li>All prices are listed in AED and exclude VAT unless otherwise noted</li>
                <li>Payments are non-refundable unless required by law</li>
                <li>Failure to pay may result in suspension or deletion of your account</li>
                <li>Price changes will be communicated with 30 days' notice</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>9. Professional Advice Disclaimer</h3>
              <p className="text-gray-700 mb-3 font-medium">Critical Limitation: Arbaah provides software tools only and does not provide:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Professional accounting advice</li>
                <li>Tax preparation services</li>
                <li>Legal compliance guidance</li>
                <li>Financial consulting</li>
                <li>Audit services</li>
              </ul>
              <p className="text-gray-700">You should consult with qualified professionals for accounting, tax, and legal matters. Any automated calculations, categorizations, or compliance suggestions are tools only and not professional advice.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>10. Service Notifications & Regulatory Alerts</h3>
              <p className="text-gray-700 mb-3">By using Arbaah, you agree to receive system-generated alerts and non-marketing emails related to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Upcoming tax filing deadlines</li>
                <li>New or updated UAE government regulations</li>
                <li>TRN/VAT compliance changes</li>
                <li>Expiring licenses or document reminders</li>
                <li>Platform updates related to legal compliance</li>
                <li>Critical system notifications</li>
              </ul>
              <p className="text-gray-700">These messages are essential service communications and cannot be disabled without deactivating your account.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>11. Disclaimer on System Alerts and Notifications</h3>
              <p className="text-gray-700 mb-3">
                While Arbaah provides automated alerts and reminders, these are offered as a courtesy only and are not guaranteed to be complete, timely, or legally sufficient.
              </p>
              <p className="text-gray-700 mb-3 font-medium">We do not guarantee:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>That any specific tax or regulatory obligation will be mentioned</li>
                <li>That alerts will be delivered on time or at all</li>
                <li>That information reflects the most recent government changes</li>
                <li>Accuracy of automated compliance suggestions</li>
              </ul>
              <p className="text-gray-700">You are solely responsible for tracking and complying with all applicable business, tax, and licensing requirements under UAE law.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>12. Limitation of Liability</h3>
              <p className="text-gray-700 mb-3">To the fullest extent permitted under UAE law, Arbaah shall not be liable for any direct, indirect, incidental, or consequential damages, including but not limited to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Loss of profits or business opportunities</li>
                <li>Mistakes in tax calculations or financial reporting</li>
                <li>Missed deadlines for license or VAT filings</li>
                <li>Data loss or service interruptions</li>
                <li>Actions taken by third-party tax or government agencies</li>
                <li>Decisions made based on platform data or suggestions</li>
              </ul>
              <p className="text-gray-700 font-medium">Maximum Liability: Our total liability shall not exceed the amount you paid for the service in the 12 months preceding the claim.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>13. Technical Infrastructure and Responsibility</h3>
              <p className="text-gray-700 mb-3">
                Arbaah software is operated by Arbaah, but all technical infrastructure, cybersecurity, and data hosting operations are exclusively handled by:
              </p>
              <div className="bg-gray-100 p-4 rounded-lg mb-3">
                <p className="text-gray-800 font-medium">
                  Amaldar L.L.C-FZ<br />
                  Meydan Grandstand<br />
                  Meydan Road, Dubai, U.A.E
                </p>
              </div>
              <p className="text-gray-700 mb-3 font-medium">Technical Responsibility Limitation:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Arbaah shall not be held liable for technical failures, data breaches, cybersecurity incidents, unauthorized access, or system errors</li>
                <li>All infrastructure and cybersecurity responsibilities rest with Amaldar L.L.C-FZ</li>
                <li>Users acknowledge this arrangement and limitation of Arbaah's technical liability</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>14. Force Majeure</h3>
              <p className="text-gray-700">
                We shall not be liable for any failure to perform due to events beyond our reasonable control, including but not limited to natural disasters, government actions, war, terrorism, internet failures, or other force majeure events.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>15. Intellectual Property</h3>
              <p className="text-gray-700">
                All software code, trademarks, text, voice systems, and design are the intellectual property of Arbaah. Unauthorized use, reproduction, or distribution is strictly prohibited.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>16. Termination</h3>
              <p className="text-gray-700 mb-3"><strong>By Us:</strong> We reserve the right to suspend or terminate your access immediately if you breach these Terms or applicable laws.</p>
              <p className="text-gray-700 mb-3"><strong>By You:</strong> You may cancel your subscription at any time through your account settings. No refunds will be provided for partial billing periods.</p>
              <p className="text-gray-700"><strong>Effect of Termination:</strong> Upon termination, your access will cease, and your data will be deleted after 30 days unless legally required to retain it.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>17. Audit Rights</h3>
              <p className="text-gray-700">
                You acknowledge that financial software may be subject to audit requirements. You agree to maintain adequate records and provide reasonable cooperation if audits are required by law.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>18. Governing Law & Jurisdiction</h3>
              <p className="text-gray-700">
                These Terms are governed by the laws of the United Arab Emirates. Any disputes shall be subject to the exclusive jurisdiction of the courts of Dubai.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>19. Changes to Terms</h3>
              <p className="text-gray-700">
                We may update these Terms to reflect legal or operational changes. You will be notified of material changes via email or platform notification at least 30 days in advance.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>20. Contact Information</h3>
              <p className="text-gray-700">For support or legal questions, contact:</p>
              <p className="text-gray-700 font-medium">📧 hello@arbaah.ae</p>
              <p className="text-gray-700 mt-3 text-sm">Last Updated: 27.05.2025</p>
            </section>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 md:p-8 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full md:w-auto px-6 py-3 rounded-lg font-medium text-white transition-colors"
            style={{ backgroundColor: colors.primary }}
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Modal Component
const PrivacyModal = ({ onClose }) => {
  const colors = {
    primary: "#122721",
    background: "#6cf168",
    accent: "#36893B",
    light: "#f8f9fa",
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 md:p-8 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-green-600" />
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: colors.primary }}>Privacy Policy</h2>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 md:px-8 md:py-8">
          <div className="prose prose-sm md:prose max-w-none">
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm md:text-base font-semibold text-green-800 mb-1">Effective Date: 27.05.2025</p>
              <p className="text-sm text-green-700">
                Company: Arbaah<br />
                Platform: www.arbaah.ae<br />
                Legal Basis: UAE Federal Decree-Law No. 45 of 2021 on Personal Data Protection (PDPL)<br />
                Email: hello@arbaah.ae
              </p>
            </div>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>1. Commitment to Privacy</h3>
              <p className="text-gray-700 mb-3">
                We value your privacy and are committed to protecting your personal and business data in full compliance with the UAE Personal Data Protection Law (PDPL).
              </p>
              <p className="text-gray-700 font-medium">
                Our Promise: Arbaah does not sell, trade, or share your data with any third party for marketing or commercial purposes, unless required by UAE law.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>2. Data Controller and Processor</h3>
              <p className="text-gray-700 mb-2"><strong>Data Controller:</strong> Arbaah (Trade License: 1511536)</p>
              <p className="text-gray-700"><strong>Data Processor:</strong> Amaldar L.L.C-FZ (infrastructure and hosting services only)</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>3. Age Restrictions</h3>
              <p className="text-gray-700">
                Our services are intended for business use only. You must be at least 18 years old to use our platform. We do not knowingly collect personal data from individuals under 18 years of age.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>4. Data We Collect</h3>
              <p className="text-gray-700 mb-3">We collect only the minimum necessary data to operate our platform:</p>
              
              <p className="text-gray-700 font-medium mb-2">Business Information:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Company name, trade license number, and TRN</li>
                <li>Business email, phone number, and contact details</li>
                <li>Billing address and payment information</li>
                <li>Authorized user names and contact information</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Financial Data:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Uploaded receipts, invoices, and financial documents</li>
                <li>Voice recordings for voice-to-invoice features</li>
                <li>Transaction data and categorizations</li>
                <li>VAT and tax-related information</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Technical Data:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>IP address and browser type (for security purposes)</li>
                <li>Device information and operating system</li>
                <li>Usage patterns and feature interactions</li>
                <li>Login timestamps and session data</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Communication Data:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Support requests and correspondence</li>
                <li>Platform notifications and alerts</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>5. How We Use Your Data</h3>
              <p className="text-gray-700 mb-3">Your data is used exclusively to:</p>
              
              <p className="text-gray-700 font-medium mb-2">Primary Services:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Generate and store VAT-compliant invoices</li>
                <li>Scan, categorize, and archive receipts</li>
                <li>Provide real-time financial dashboards</li>
                <li>Store documents and provide automated reminders</li>
                <li>Process voice inputs for invoice creation</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Platform Operations:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Authenticate users and secure accounts</li>
                <li>Send business reminders and compliance notifications</li>
                <li>Improve platform stability and user experience</li>
                <li>Provide customer support</li>
                <li>Ensure compliance with UAE accounting and tax laws</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Legal Compliance:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Meet UAE tax and business reporting requirements</li>
                <li>Respond to lawful requests from authorities</li>
                <li>Maintain audit trails as required by law</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>6. Legal Basis for Processing</h3>
              <p className="text-gray-700 mb-3">We process your data under the following lawful bases as defined by PDPL:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Consent:</strong> When you register for the platform or opt-in to specific features</li>
                <li><strong>Contract:</strong> To provide the services you have requested and subscribed to</li>
                <li><strong>Legal Obligation:</strong> To comply with UAE tax, business, and data protection regulations</li>
                <li><strong>Legitimate Interest:</strong> To improve platform security and functionality</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>7. Data Sharing and Disclosure</h3>
              <p className="text-gray-700 mb-3">We do not sell or share your data with third parties except in the following limited circumstances:</p>
              
              <p className="text-gray-700 font-medium mb-2">Service Providers:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Amaldar L.L.C-FZ (infrastructure hosting and technical operations only)</li>
                <li>Payment processors (for subscription billing only)</li>
                <li>Email service providers (for essential platform communications only)</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Legal Requirements:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>UAE government authorities when legally required</li>
                <li>Law enforcement agencies under valid legal process</li>
                <li>Tax authorities for compliance verification</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Business Transfers:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>In the event of a merger, acquisition, or sale of assets (with notice to users)</li>
              </ul>

              <p className="text-gray-700">All service providers are bound by confidentiality agreements and data protection requirements.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>8. International Data Transfers</h3>
              <p className="text-gray-700">
                Your data is primarily stored within the UAE. Any international data transfers (if necessary) will be conducted in compliance with PDPL requirements and with appropriate safeguards in place.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>9. Data Hosting and Security</h3>
              <p className="text-gray-700 mb-3"><strong>Hosting Partner:</strong> All user data is securely stored and managed by our infrastructure partner:</p>
              <div className="bg-gray-100 p-4 rounded-lg mb-3">
                <p className="text-gray-800 font-medium">
                  Amaldar L.L.C-FZ<br />
                  Meydan Grandstand<br />
                  Meydan Road, Dubai, U.A.E
                </p>
              </div>
              <p className="text-gray-700 mb-3">
                <strong>Important Clarification:</strong> Amaldar acts strictly as a data processor on behalf of Arbaah and is contractually limited to providing technical services. They do not have independent access to, use of, or control over your data.
              </p>
              
              <p className="text-gray-700 font-medium mb-2">Security Measures:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>SSL/TLS encryption for data transmission</li>
                <li>AES encryption for data storage</li>
                <li>Cloud infrastructure hosted in secure UAE-based data centers</li>
                <li>Regular security audits and penetration testing</li>
                <li>Multi-factor authentication capabilities</li>
                <li>Access logging and audit trails</li>
                <li>Automated backup and disaster recovery procedures</li>
                <li>Network security and firewall protection</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>10. Cookies and Tracking Technologies</h3>
              <p className="text-gray-700 mb-3">Arbaah uses the following cookies and tracking technologies:</p>
              
              <p className="text-gray-700 font-medium mb-2">Essential Cookies:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Authentication and session management</li>
                <li>Security and fraud prevention</li>
                <li>Platform functionality and user preferences</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Analytics Cookies (Optional):</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Platform usage statistics and performance monitoring</li>
                <li>Feature usage analytics to improve user experience</li>
              </ul>

              <p className="text-gray-700">You can manage cookie preferences through your browser settings. Disabling essential cookies may impact platform functionality.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>11. Automated Decision-Making</h3>
              <p className="text-gray-700 mb-3">Our platform uses automated systems for:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Receipt categorization and data extraction (OCR)</li>
                <li>Voice-to-text conversion for invoice creation</li>
                <li>Basic financial calculations and VAT computations</li>
                <li>Expense categorization suggestions</li>
              </ul>
              <p className="text-gray-700">These automated processes are designed to assist you but do not make decisions that significantly affect your legal or financial standing without human oversight.</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>12. Data Retention</h3>
              <p className="text-gray-700 mb-3">We retain your data for the following periods:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Active Accounts:</strong> Data is retained for as long as your account remains active</li>
                <li><strong>Closed Accounts:</strong> Data is deleted within 30 days of account closure, except where longer retention is required by law</li>
                <li><strong>Legal Requirements:</strong> Financial and tax-related data may be retained for 5 years as required by UAE tax law</li>
                <li><strong>Communication Records:</strong> Support communications are retained for 2 years</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>13. Your Rights Under PDPL</h3>
              <p className="text-gray-700 mb-3">As a data subject under UAE PDPL, you have the following rights:</p>
              
              <p className="text-gray-700 font-medium mb-2">Access Rights:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Request access to your personal data</li>
                <li>Obtain a copy of your data in a commonly used format</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Correction Rights:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Request correction of inaccurate or incomplete data</li>
                <li>Update your account information at any time</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Deletion Rights:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Request deletion of your data (subject to legal retention requirements)</li>
                <li>Account closure and data deletion</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Portability Rights:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Request your data in a portable format</li>
                <li>Transfer your data to another service provider</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Objection Rights:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Object to processing based on legitimate interests</li>
                <li>Withdraw consent for non-essential processing</li>
              </ul>

              <p className="text-gray-700 font-medium mb-2">Restriction Rights:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Request restriction of processing in certain circumstances</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>14. Data Breach Notification</h3>
              <p className="text-gray-700 mb-3">In the event of a data breach that poses a risk to your rights and freedoms, we will:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Notify the UAE Data Office within 72 hours of discovery</li>
                <li>Inform affected users without undue delay</li>
                <li>Provide clear information about the nature of the breach</li>
                <li>Outline steps taken to address the breach and prevent recurrence</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>15. Exercising Your Rights</h3>
              <p className="text-gray-700 mb-3">To exercise any of your rights under PDPL:</p>
              
              <p className="text-gray-700 mb-2"><strong>Contact Method:</strong> Email hello@arbaah.ae with your request</p>
              
              <p className="text-gray-700 font-medium mb-2">Required Information:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Your full name and company name</li>
                <li>Account email address</li>
                <li>Specific nature of your request</li>
                <li>Identity verification may be required</li>
              </ul>
              
              <p className="text-gray-700 mb-2"><strong>Response Time:</strong> We will respond to your request within 30 days</p>
              <p className="text-gray-700"><strong>No Cost:</strong> Exercising your rights is free of charge unless requests are manifestly unfounded or excessive</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>16. Complaints and Appeals</h3>
              <p className="text-gray-700 mb-3">If you are not satisfied with how we handle your personal data or privacy request:</p>
              
              <p className="text-gray-700 mb-2"><strong>Internal Appeal:</strong> Contact hello@arbaah.ae to escalate your concern</p>
              
              <p className="text-gray-700 mb-2"><strong>Regulatory Complaint:</strong> You may lodge a complaint with the UAE Data Office:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Website: tdra.gov.ae</li>
                <li>Email: dataprotection@tdra.gov.ae</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>17. Data Protection Officer</h3>
              <p className="text-gray-700">
                For privacy-related questions or concerns, you may contact our data protection representative at: hello@arbaah.ae
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>18. Changes to This Privacy Policy</h3>
              <p className="text-gray-700 mb-3">We may update this Privacy Policy to reflect:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Changes in UAE data protection law</li>
                <li>New platform features or services</li>
                <li>Updates to our data processing practices</li>
              </ul>
              
              <p className="text-gray-700 font-medium mb-2">Notification of Changes:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Material changes will be communicated via email</li>
                <li>Updates will be posted on our platform with advance notice</li>
                <li>Continued use constitutes acceptance of updated terms</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>19. Contact Information</h3>
              <p className="text-gray-700 mb-2">For privacy questions, data subject requests, or concerns:</p>
              <p className="text-gray-700 mb-2"><strong>Email:</strong> hello@arbaah.ae</p>
              <p className="text-gray-700 mb-2"><strong>Subject Line:</strong> Privacy Inquiry</p>
              <p className="text-gray-700"><strong>Response Time:</strong> Within 5 business days for initial response</p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-3" style={{ color: colors.primary }}>20. Effective Date and Governing Law</h3>
              <p className="text-gray-700 mb-3">
                This Privacy Policy is effective as of 27.05.2025 and is governed by UAE Federal Decree-Law No. 45 of 2021 on Personal Data Protection (PDPL) and other applicable UAE laws.
              </p>
              <p className="text-gray-700 text-sm">Last Updated: 27.05.2025</p>
            </section>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 md:p-8 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full md:w-auto px-6 py-3 rounded-lg font-medium text-white transition-colors"
            style={{ backgroundColor: colors.primary }}
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

export default Footer;